import React, { useMemo } from "react";
import { TrapStatusData } from "../../types";
import { useListTrapSitesQuery } from "../../actions/trapsiteInQuery";
import "./index.css";
import { useDeviceUuids } from "../../hooks/useAppAlerts";
import TrapStatus from "./TrapStatus";
import { useFallbackPollingInterval } from "../../providers/RealtimeProvider";

// Only while the realtime socket is down; see useFallbackPollingInterval.
export const TRAP_STATUS_POLL_MS = 60000;

// By trap_site name then by device name, with unknowns at the end
const sortByLocation = (a: TrapStatusData, b: TrapStatusData) =>
  a?.trapsite_name === "Unknown Location" &&
  b?.trapsite_name !== "Unknown Location"
    ? 1
    : b?.trapsite_name === "Unknown Location" &&
        a?.trapsite_name !== "Unknown Location"
      ? -1
      : (a?.trapsite_name || "").localeCompare(b?.trapsite_name || "") ||
        (a?.name || "").localeCompare(b?.name || "");

/**
 * `org` renders another organization's traps, for the superuser panel.
 *
 * `orgNames` labels each trap with its organization and groups by it, for the
 * panel's All view where the list spans organizations.
 */
const Index: React.FC<{ org?: string; orgNames?: Map<string, string> }> = ({
  org,
  orgNames,
}) => {
  const pollingInterval = useFallbackPollingInterval(TRAP_STATUS_POLL_MS);
  const {
    deviceUuids,
    devices,
    isLoadingDevices: isDevicesLoading,
  } = useDeviceUuids({ pollingInterval, org });

  const deviceUuidSet = useMemo(() => new Set(deviceUuids), [deviceUuids]);

  const { data: trapSitesData, isLoading: isTrapSiteLoading } =
    useListTrapSitesQuery(org ? { org } : undefined);

  const trapStatusData = useMemo(() => {
    const result: TrapStatusData[] = devices
      ?.map((device) => {
        // this is a filter to make sure we only show devices that are in the current location
        if (!deviceUuidSet.has(device?.uuid || "")) {
          return null;
        }
        const trapsite =
          trapSitesData?.items?.find((t) => {
            return t._id === device.trap_site_id;
          }) || {};
        return {
          _id: device.uuid,
          name: device?.display_name || device?.uuid || "",
          trapsite_name: trapsite.display_name || "Unknown Location",
          ...(orgNames && {
            org_name: orgNames.get(device.org ?? "") || device.org || "",
          }),
          gps:
            trapsite?.gps?.lat && trapsite?.gps?.lon
              ? `${trapsite.gps.lat.toFixed(4)}, ${trapsite.gps.lon.toFixed(4)}`
              : "Unknown GPS",
          status: device?.status || "offline",
          status_updated_at: device?.status_updated_at || new Date(),
          state: device?.state,
          state_updated_at: device?.state_updated_at,
        };
      })
      // filter out nulls
      .filter((item) => item !== null)
      // Sort by organization (All view only), then trap_site name, then device
      // name, with unknown locations at the end of each organization
      .sort(
        (a, b) =>
          (a?.org_name || "").localeCompare(b?.org_name || "") ||
          sortByLocation(a as TrapStatusData, b as TrapStatusData),
      ) as TrapStatusData[];

    return result;
  }, [devices, trapSitesData, deviceUuidSet, orgNames]);

  const handleClickDialog = (id: string) => {
    // TODO: Handle click on alert dialog
  };

  return (
    <TrapStatus
      loading={isTrapSiteLoading || isDevicesLoading}
      trapStatusData={trapStatusData}
      handleClickDialog={handleClickDialog}
    />
  );
};

export default Index;
